import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, Redirect } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, Mail, BarChart3, Calendar, Users, Shield, Zap, Trophy } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/user-friendly-messages";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { PrivacyPolicyDialog } from "@/components/ui/privacy-policy-dialog";
import { EmailVerificationDialog } from "@/components/ui/email-verification-dialog";

const emailVerificationSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type EmailVerificationFormValues = z.infer<typeof emailVerificationSchema>;

export default function RegisterPage() {
  const { user } = useAuth();
  const [location] = useLocation();
  const { toast } = useToast();
  
  const urlParams = new URLSearchParams(location.split('?')[1] || '');
  const planId = urlParams.get('plan');
  const planName = urlParams.get('planName');
  const priceId = urlParams.get('priceId');
  
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showEmailVerificationDialog, setShowEmailVerificationDialog] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");

  const emailVerificationForm = useForm<EmailVerificationFormValues>({
    resolver: zodResolver(emailVerificationSchema),
    defaultValues: {
      email: "",
    },
  });

  // Email verification mutation
  const emailVerificationMutation = useMutation({
    mutationFn: async (data: EmailVerificationFormValues) => {
      return await apiRequest("/api/auth/send-verification", {
        method: "POST",
        body: data,
      });
    },
    onSuccess: (data, variables) => {
      // Store the email and show the prominent dialog instead of toast
      setVerificationEmail(variables.email);
      setShowEmailVerificationDialog(true);
      emailVerificationForm.reset();
    },
    onError: (error: any) => {
      const errorMessage = getUserFriendlyError(error);
      toast({
        title: "Error",
        description: errorMessage.description || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const onEmailVerification = async (data: EmailVerificationFormValues) => {
    emailVerificationMutation.mutate(data);
  };

  const handleGoogleLogin = () => {
    let googleUrl = "/api/auth/google";
    if (planId && planName && priceId) {
      googleUrl += `?plan=${planId}&planName=${planName}&priceId=${priceId}`;
    }
    window.location.href = googleUrl;
  };

  // If already logged in, redirect to the dashboard (moved after all hooks)
  if (user) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <>
      <Helmet>
        <title>Register for SaaSGama | Start Managing SaaS Smarter</title>
        <meta name="description" content="Create your SaaSGama account to track software usage, manage licenses, reduce SaaS costs, automate workflows and gain AI-powered insights today." />
        <meta name="keywords" content="SaaS Management Software, License Management, SaaS Cost Optimization, SaaS Spend Management, AI-Powered SaaS Management" />
        <link rel="canonical" href="https://saasgama.com/register" />
      </Helmet>

      <div className="h-screen overflow-hidden bg-white">
        {/* Split Layout Container */}
        <div className="flex h-full">
          {/* Left Side - Hero Content (hidden on mobile) */}
          <div className="hidden lg:flex lg:w-3/5 bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 flex-col justify-center px-16 py-12 relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 to-blue-600/10"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full translate-y-24 -translate-x-24"></div>
            
            <div className="relative z-10 max-w-lg">
              <SaaSGamaLogo size="lg" className="mb-8" />
              
              <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Create Your SaaSGama Account
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Join companies saving an average of 30% on their software costs. 
                Get started in under 60 seconds - no credit card required.
              </p>
              
              {/* Key Benefits */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Cost Optimization</h3>
                    <p className="text-sm text-gray-600">Identify unused licenses & duplicate subscriptions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Renewal Tracking</h3>
                    <p className="text-sm text-gray-600">Never miss important renewal dates again</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Team Management</h3>
                    <p className="text-sm text-gray-600">Assign licenses and track usage across teams</p>
                  </div>
                </div>
              </div>
              
              {/* Social Proof */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/60 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">A</div>
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">B</div>
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">C</div>
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-700 text-xs font-bold ring-2 ring-white">+10K</div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">10,000+ companies trust SaaSGama</p>
                    <p className="text-xs text-gray-600">Average savings: 30% monthly</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div>
                    <div className="text-lg font-bold text-blue-600">$2.5M+</div>
                    <div className="text-gray-600 text-xs">Saved monthly</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-green-600">99.9%</div>
                    <div className="text-gray-600 text-xs">Uptime</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-600">4.8★</div>
                    <div className="text-gray-600 text-xs">User rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Center Divider */}
          <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
          
          {/* Right Side - Form */}
          <div className="flex-1 lg:w-2/5 flex flex-col justify-center px-6 sm:px-12 lg:px-20 relative">
            {/* Mobile Header */}
            <div className="lg:hidden flex justify-between items-center py-6 border-b border-gray-200 mb-8">
              <SaaSGamaLogo size="lg" />
              <div className="text-base text-gray-600">
                Have an account?{" "}
                <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Sign in
                </a>
              </div>
            </div>
            
            {/* Desktop Header */}
            <div className="hidden lg:block absolute top-6 right-6">
              <div className="text-base text-gray-600">
                Have an account?{" "}
                <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Sign in
                </a>
              </div>
            </div>

            <div className="max-w-sm mx-auto w-full">
              {/* Mobile Hero */}
              <div className="lg:hidden text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  Create Your SaaSGama Account
                </h1>
                <p className="text-gray-600">
                  Start optimizing your SaaS spend today
                </p>
              </div>

              {/* Desktop Form Title */}
              <div className="hidden lg:block text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Get started free
                </h2>
                <p className="text-gray-600 text-lg">
                  Create your account in seconds - no credit card required
                </p>
              </div>

              {/* Special Plan Badge */}
              {planName && (
                <div className="mb-8 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-3">
                    <Trophy className="h-6 w-6 text-blue-600" />
                    <span className="font-semibold text-blue-900 text-lg">Selected Plan: {planName}</span>
                  </div>
                  <p className="text-blue-700 mt-2">Complete registration to activate your plan</p>
                </div>
              )}


              {/* Google OAuth Button */}
              <Button 
                onClick={handleGoogleLogin}
                className="w-full h-14 bg-[#1a73e8] hover:bg-[#1557b0] text-white font-medium text-base flex items-center rounded-xl border border-[#1a73e8] transition-colors duration-200 relative p-0 overflow-hidden mb-6 shadow-lg"
                type="button"
              >
                <div className="w-14 h-14 bg-white flex items-center justify-center flex-shrink-0">
                  <FcGoogle className="w-6 h-6" />
                </div>
                <div className="flex-1 flex items-center justify-center -ml-7">
                  <span className="font-medium text-base">Continue with Google</span>
                </div>
              </Button>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-6 text-gray-500 uppercase font-medium tracking-wide">Or</span>
                </div>
              </div>

              {/* Email Verification Form */}
              <Form {...emailVerificationForm}>
                <form onSubmit={emailVerificationForm.handleSubmit(onEmailVerification)} className="space-y-4">
                  <FormField
                    control={emailVerificationForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder="Enter your work email" 
                            {...field}
                            className="h-12 text-base border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-md"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full h-14 text-base font-medium bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200" 
                    disabled={emailVerificationMutation.isPending}
                  >
                    {emailVerificationMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending verification...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Continue with Email →
                      </>
                    )}
                  </Button>
                </form>
              </Form>

              {/* Trust Indicators */}
              <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-500" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-500" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-purple-500" />
                  <span>Cancel anytime</span>
                </div>
              </div>

              {/* Mobile Social Proof */}
              <div className="lg:hidden mt-8 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700 mb-3">Join 10,000+ companies</p>
                  <div className="flex justify-center gap-6 text-sm text-gray-600">
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">30%</div>
                      <div>Avg Savings</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">$2.5M+</div>
                      <div>Saved Monthly</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-3 px-6">
          <div className="text-center">
            <div className="text-xs text-gray-500">
              By creating an account, you agree to our{" "}
              <button 
                onClick={() => setShowPrivacyPolicy(true)}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Privacy Policy
              </button>
              {" "}and Terms of Service
            </div>
          </div>
        </div>

        {/* Privacy Policy Dialog */}
        <PrivacyPolicyDialog 
          open={showPrivacyPolicy} 
          onOpenChange={setShowPrivacyPolicy} 
        />

        {/* Email Verification Dialog */}
        <EmailVerificationDialog
          open={showEmailVerificationDialog}
          onOpenChange={setShowEmailVerificationDialog}
          email={verificationEmail}
        />

      </div>
    </>
  );
}
